import { create } from 'zustand'
import { toast } from 'sonner'
export interface DemoStep {
  id: string
  title: string
  description: string
  targetSelector: string
  actionType: 'highlight' | 'navigate' | 'modal' | 'toast'
  delayMs: number
  route?: string
  toastMessage?: string
}
interface DemoState {
  isActive: boolean
  isPaused: boolean
  currentStep: number
  steps: DemoStep[]
  startDemo: () => void
  nextStep: () => void
  previousStep: () => void
  resetDemo: () => void
  skipDemo: () => void
  togglePause: () => void
  setCurrentStep: (step: number) => void
}
const demoSteps: DemoStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Quad Atlas',
    description:
      'Quad Atlas is your unified control center for account intelligence, powered by Sprinklenet Knowledge Spaces.',
    targetSelector: '[data-demo="login-screen"]',
    actionType: 'highlight',
    delayMs: 2000,
  },
  {
    id: 'dashboard',
    title: 'Account Intelligence Dashboard',
    description:
      'Search and filter accounts by health status, industry, and products. Each account card shows real-time KPIs and sync status.',
    targetSelector: '[data-demo="search-bar"]',
    actionType: 'navigate',
    route: '/',
    delayMs: 2500,
  },
  {
    id: 'account-card',
    title: 'Client Account Overview',
    description:
      'Every client has dedicated chatbots: QuadChat (internal) for your team and Quad Assist (external) for partners.',
    targetSelector: '[data-demo="account-card"]',
    actionType: 'highlight',
    delayMs: 3000,
  },
  {
    id: 'knowledge-spaces',
    title: 'Knowledge Spaces Integration',
    description:
      'Click any Knowledge Space badge to see documents, collaborators, and sync settings. All data lives in Sprinklenet.',
    targetSelector: '[data-demo="knowledge-space-badge"]',
    actionType: 'highlight',
    delayMs: 2500,
  },
  {
    id: 'space-modal',
    title: 'Knowledge Space Details',
    description:
      'View documents, bound chatbots, collaborators, and settings. Sync data in real-time with Sprinklenet Knowledge Spaces.',
    targetSelector: '[data-demo="space-modal"]',
    actionType: 'modal',
    delayMs: 3000,
  },
  {
    id: 'spaces-explorer',
    title: 'Spaces Explorer',
    description:
      'Browse all Knowledge Spaces across your accessible accounts. Filter by visibility (Internal vs External).',
    targetSelector: '[data-demo="spaces-nav"]',
    actionType: 'navigate',
    route: '/spaces',
    delayMs: 2500,
  },
  {
    id: 'analytics',
    title: 'Analytics & Insights',
    description:
      'Visualize account health trends, Knowledge Space distribution, and service usage across your portfolio.',
    targetSelector: '[data-demo="analytics-nav"]',
    actionType: 'navigate',
    route: '/analytics',
    delayMs: 2500,
  },
  {
    id: 'settings',
    title: 'Settings & Sync Control',
    description:
      'Manage roles, run full Knowledge Space syncs, and view audit logs. Control access and permissions from here.',
    targetSelector: '[data-demo="settings-nav"]',
    actionType: 'navigate',
    route: '/settings',
    delayMs: 2500,
  },
  {
    id: 'role-switcher',
    title: 'Role-Based Access Control',
    description:
      'Switch between Org Admin, Group Admin, Account Admin, and Account Member to see different permission levels.',
    targetSelector: '[data-demo="user-menu"]',
    actionType: 'highlight',
    delayMs: 2500,
  },
  {
    id: 'complete',
    title: 'Demo Complete',
    description:
      'Quad Atlas unifies account intelligence, Knowledge Spaces, and chatbot deployment into one powerful platform.',
    targetSelector: '[data-demo="demo-controller"]',
    actionType: 'toast',
    toastMessage:
      '🎉 Demo Complete — Quad Atlas is ready to transform your account management!',
    delayMs: 2000,
  },
]
export const useDemoMode = create<DemoState>((set, get) => ({
  isActive: false,
  isPaused: false,
  currentStep: 0,
  steps: demoSteps,
  startDemo: () => {
    set({ isActive: true, isPaused: false, currentStep: 0 })
    toast.success('Demo Mode Started', {
      description: 'Follow the guided walkthrough to explore Quad Atlas',
      duration: 3000,
    })
  },
  nextStep: () => {
    const { currentStep, steps } = get()
    if (currentStep < steps.length - 1) {
      set({ currentStep: currentStep + 1 })
    } else {
      get().resetDemo()
    }
  },
  previousStep: () => {
    const { currentStep } = get()
    if (currentStep > 0) {
      set({ currentStep: currentStep - 1 })
    }
  },
  resetDemo: () => {
    set({ isActive: false, isPaused: false, currentStep: 0 })
    toast.info('Demo Reset', {
      description: 'Demo mode has been reset',
      duration: 2000,
    })
  },
  skipDemo: () => {
    set({ isActive: false, isPaused: false, currentStep: 0 })
    toast.info('Demo Skipped', {
      description: 'You can restart the demo anytime from Settings',
      duration: 2000,
    })
  },
  togglePause: () => {
    set((state) => ({ isPaused: !state.isPaused }))
  },
  setCurrentStep: (step: number) => {
    set({ currentStep: step })
  },
}))
